import React, { useEffect, useState } from 'react';
import { DeliveryOptionCard, OrderBar, BackPage, WarningContainer, Spinner } from 'components';
import { DeliveryOptionPageContent } from 'services/content.js';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as Types from 'helpers/types';
import styles from './styles.module.css';
import alertTriangleIcon from 'assets/alert-triangle.svg';
import { FRIDGE_FULFILLMENT_SCREEN, CONTROLLED_DRUG_SCREEN, GA_EVENTS } from 'helpers/monitoringEvents';
import { useAnalytics, useMedicationDeliveryInfo, useMonitoringEvents, useIsOffboardingPharmacy } from 'hooks';
import getDeliveryOptions from './deliveryOptions';
import { getBasketMedicationName } from 'helpers/helperFunctions';

const DeliveryOptions = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [postLog] = useMonitoringEvents();
  const [, postGoogleTag] = useAnalytics();
  const {
    paymentJourney: {
      started: startedJourney,
      basket,
      selectedPharmacyDetails,
      gpRegisteredInRange,
      pharmaciesInRange,
      dspExclusion,
    },
    orderHistory,
    medication: { medication },
  } = useSelector((state) => state);
  const [offboardingData, loading, getIsOffboarding] = useIsOffboardingPharmacy();

  useEffect(() => {
    !orderHistory.loading && getIsOffboarding(orderHistory.orders[0]?.fulfillment.pharmacy_ods_code || null);
  }, [orderHistory]);

  const [deliveryInfoLoading, getMedicationInfo] = useMedicationDeliveryInfo(getBasketMedicationName(basket, medication));
  const [medicationDeliveryInfo, setMedicationDeliveryInfo] = useState({})

  useEffect(() => {
    const getMedInfo = async () => {
      const response = await getMedicationInfo(getBasketMedicationName(basket, medication));
      setMedicationDeliveryInfo(response);
    }
    getMedInfo();
  },[])

  let availableDeliveryOptions = [];
  const selectedPharmacyUser =
    selectedPharmacyDetails?.delivery_available && selectedPharmacyDetails?.delivery_enabled && gpRegisteredInRange;

  const deliveryOptions = getDeliveryOptions(selectedPharmacyUser, pharmaciesInRange);

  if (selectedPharmacyUser) {
    availableDeliveryOptions = ['collectFromPharmacy', 'pharmacyDelivery'];
  } else {
    availableDeliveryOptions = ['collectFromPharmacy'];
    if (
      (pharmaciesInRange?.length > 0 && !selectedPharmacyDetails?.ods_code) ||
      (selectedPharmacyDetails?.ods_code &&
        selectedPharmacyDetails?.delivery_available &&
        selectedPharmacyDetails?.delivery_enabled)
    )
      availableDeliveryOptions.push('pharmacyDelivery');
    if (Object.values(medicationDeliveryInfo).every((i) => !i) && !dspExclusion)
      availableDeliveryOptions.push('homeDelivery');
  }

  const displayWarning = () => {
    const allWarnings = {
      ...{ offboarding: offboardingData?.showFulfillmentMessage },
      ...medicationDeliveryInfo,
    };
    const warning = Object.entries(allWarnings).find(([_, value]) => {
      return value;
    })?.[0];

    if (warning) {
      let { heading, text } = DeliveryOptionPageContent.warningMessage[warning];
      if (warning === 'offboarding') {
        text = text.split(' ').map((x) => {
          const word =
            x === '{pharmacyName},' ? (
              <span key={x} className={styles.offboardingPharmacyName}>{`${
                offboardingData?.pharmacyName || orderHistory?.orders[0]?.fulfillment.pharmacy_name
              }, `}</span>
            ) : (
              `${x} `
            );
          return word;
        });
        return <WarningContainer type="warning" icon={alertTriangleIcon} heading={heading} text={text} />;
      } else {
        return <WarningContainer type="warning" icon={alertTriangleIcon} heading={heading} text={text} />;
      }
    } else {
      return null;
    }
  };

  useEffect(() => {
    if (!startedJourney) history.push('/pharmacy/dashboard');
  }, [history, startedJourney]);

  useEffect(() => {
    if (medicationDeliveryInfo?.fridgeline) {
      postLog(FRIDGE_FULFILLMENT_SCREEN);
      postGoogleTag(GA_EVENTS.FRIDGE_LINE.FRIDGE_FULFILLMENT_SCREEN);
    }
    if (medicationDeliveryInfo?.controlled) {
      postLog(CONTROLLED_DRUG_SCREEN);
      postGoogleTag(GA_EVENTS.CONTROLLED_DRUG.CONTROLLED_DRUG_SCREEN);
    }
  }, [medicationDeliveryInfo]);

  if (loading || deliveryInfoLoading) {
    return (
      <BackPage onBackClick={() => history.goBack()} pushTop>
        <div className={styles.loadingSpinner}>
          <Spinner />
        </div>
      </BackPage>
    );
  }

  return (
    <>
      <OrderBar currentProgress={0} />
      <BackPage onBackClick={() => history.push('/pharmacy/dashboard')} pushTop>
        <h1>{DeliveryOptionPageContent.headingText}</h1>
        {displayWarning()}
        {availableDeliveryOptions.map((deliveryOption) => (
          <DeliveryOptionCard
            key={deliveryOption}
            data={DeliveryOptionPageContent[deliveryOption]}
            deliveryOption={deliveryOptions[deliveryOption]}
            selectedPharmacyUser={selectedPharmacyUser}
            analytics={medicationDeliveryInfo}
          />
        ))}
        {Object.values(medicationDeliveryInfo).some((i) => i) && !dspExclusion ? (
          <button
            className={styles.homeDeliveryContainer}
            onClick={() => {
              dispatch({ type: Types.DELIVERY_METHOD_SET, payload: deliveryOptions.homeDelivery.payload });
              Object.entries(medicationDeliveryInfo).forEach(([key, value]) => {
                if (value) {
                  postLog(deliveryOptions.homeDelivery.event[key].logEvent);
                  postGoogleTag(deliveryOptions.homeDelivery.event[key].ga);
                }
              });
              history.push(deliveryOptions.homeDelivery.location);
            }}>
            <p>I need my prescription delivered via Royal Mail</p>
          </button>
        ) : null}
      </BackPage>
    </>
  );
};

export default DeliveryOptions;
